import { Component, ViewChild, OnInit, ElementRef, OnDestroy } from "@angular/core";
import { AuthService, GlobalsService, LoginService, SleepService, TimeclockService, FunctionLockService, JobsService, ScheduleStore, InvoicesService, AppointmentsService, LookupService } from "@services";
import { ITimeclockEntryModel, TimeclockBreakModel, TimeclockEntryModel, TimeclockStartEndModel, IScheduleAppointmentSearchModel, IScheduledAppointmentModel, ISystemLogModel } from "@models";
import { Title } from "@angular/platform-browser";
import { ChatStore, TimeclockStore } from "@stores";
import { JobEditDialogComponent } from "@app/jobs/job-components";
import { SkeditChatComponent } from "@app/chat/chat.module";
import { Subscription } from "rxjs";
import { SlickDialogComponent, SlickScreenBlockerService, SlickSearchBarComponent } from "@slick-components";
import { HttpService } from "@services/utils/http.service";
import Swal from 'sweetalert2';
import * as moment from 'moment';

@Component({
    selector: "header-bar",
    templateUrl: "header-bar.component.html",
    styleUrls: ['header-bar.component.scss'],
    providers: [AppointmentsService, TimeclockService, SlickScreenBlockerService, JobsService, InvoicesService],
    host: { 'class': 'header-bar' }
})
export class HeaderBarComponent implements OnInit, OnDestroy {

    @ViewChild("trialRef", { static: true }) trialRef: ElementRef;
    @ViewChild("chatRef", { static: false }) chatRef: SkeditChatComponent;
    @ViewChild("jobEditDialogRef", { static: true }) jobEditDialogRef: JobEditDialogComponent;
    @ViewChild("searchBarRef") searchBarRef: SlickSearchBarComponent;

    firstName: string = GlobalsService.userInfo.firstName;
    lastName: string = GlobalsService.userInfo.lastName;
    profilePictureUrl: string = GlobalsService.userInfo.profilePictureUrl;
    trialDays: number = GlobalsService.company.trialDays;
    trialStartDate: Date = GlobalsService.company.trialStartDate;
    trialEndDate: Date = GlobalsService.company.freeTrialEndDate;
    daysLeftInTrial: number = GlobalsService.company.daysLeftInTrial;
    companyName: string = GlobalsService.company.companyName;

    currentDate: Date;
    clipboardAppointment: IScheduledAppointmentModel;
    appointmentId: number;
    searchResults: IScheduleAppointmentSearchModel[];

    currentDate$: Subscription;
    clipboardAppointment$: Subscription;
    chatType$: Subscription;
    refreshCurrentEntry$: Subscription;
    unreadMessageCount$: Subscription;

    chatType: string;
    newChatMessagesCount: number = 0;
    currentTimeclockEntry: ITimeclockEntryModel;
    breaks: TimeclockStartEndModel[];

    isTimeclockExempt: boolean = GlobalsService.userInfo.timeClockExempt;
    fullName: string = GlobalsService.userInfo.fullName;

    isQPro = GlobalsService.company.isQPro;
    isLathamOrdersOnly = GlobalsService.company.isLathamOrdersOnly;
    skeditPay: boolean = GlobalsService.company.useSkeditPay;
    canAccessChat: boolean = GlobalsService.userInfo.showOnChat;

    // Logs
    showLogs = false;
    systemLogs: ISystemLogModel[] = [];
    logsLoaded = false;
    isLoadingLogs = false;
    hoverTimeout: any;

    constructor(
        public titleService: Title,
        private readonly authService: AuthService,
        private readonly loginService: LoginService,
        private readonly lookupService: LookupService,
        private readonly timeclockService: TimeclockService,
        private readonly slickScreenBlockerService: SlickScreenBlockerService,
        private timeclockStore: TimeclockStore,
        private functionLockService: FunctionLockService,
        private jobsService: JobsService,
        private appointmentsService: AppointmentsService,
        private scheduleStore: ScheduleStore,
        private chatStore: ChatStore,
        private readonly httpService: HttpService
    ) { }

    async ngOnInit() {
        this.titleService.setTitle("");

        this.currentDate = this.scheduleStore.currentDate;

        this.currentDate$ = this.scheduleStore.currentDateStore.subscribe((currentDate) => {
            this.currentDate = currentDate;
        });

        this.clipboardAppointment$ = this.scheduleStore.clipboardAppointmentStore.subscribe((clipboardAppt: IScheduledAppointmentModel) => {
            this.clipboardAppointment = clipboardAppt;
        });

        this.chatType$ = this.chatStore.chatTypeStore.subscribe(chatType => this.chatType = chatType);

        this.refreshCurrentEntry$ = this.timeclockStore.refreshCurrentEntry$.subscribe((timeclockEntryModel: ITimeclockEntryModel) => {
            this.currentTimeclockEntry = timeclockEntryModel;
        });

        this.unreadMessageCount$ = this.chatStore.unreadMessageCountSubject.subscribe(messageCount => {
            this.newChatMessagesCount = messageCount;
        });

        this.currentTimeclockEntry = await this.timeclockService.getLastTimeclockEntry(GlobalsService.userInfo.userId);

        // Trial message
        if (GlobalsService.company.isPaid === false && GlobalsService.company.isLathamOrdersOnly === false) {
            await SleepService.sleep(4000);
            (<HTMLDivElement>this.trialRef.nativeElement).classList.add("show");

            await SleepService.sleep(5000);
            (<HTMLDivElement>this.trialRef.nativeElement).classList.remove("show");
            (<HTMLDivElement>this.trialRef.nativeElement).classList.add("hide");

            await SleepService.sleep(1000);
            (<HTMLDivElement>this.trialRef.nativeElement).classList.remove("hide");
        }
    }

    ngOnDestroy() {
        this.currentDate$?.unsubscribe();
        this.clipboardAppointment$?.unsubscribe();
        this.chatType$?.unsubscribe();
        this.refreshCurrentEntry$?.unsubscribe();
        this.unreadMessageCount$?.unsubscribe();
    }

    // ====================== LOGS LOGIC ======================
    async onMouseEnter() {
        clearTimeout(this.hoverTimeout);
        this.showLogs = true;

        try {
            const params = { userName: this.firstName + " " + this.lastName };
            this.systemLogs = await this.httpService.get("/systemLogs/GetSystemLogsByUserName", params);
        } catch (error) {
            console.error('Failed to load system logs', error);
            this.systemLogs = [];
        } finally {
            this.isLoadingLogs = false;
        }
    }

    onMouseLeave() {
        this.hoverTimeout = setTimeout(() => {
            this.showLogs = false;
        }, 200);
    }

    getLogLink(log: ISystemLogModel): string[] | null {
        if (log.jobId) return ['/jobs'];
        if (log.customerId) return ['/customers'];
        if (log.invoiceId) return ['/invoices'];
        if (log.paymentId) return ['/payments'];
        if (log.lathamOrderId) return ['/latham-orders'];
        return null;
    }

    getLogQueryParams(log: ISystemLogModel): any {
        if (log.jobId) return { jobId: log.jobId };
        if (log.customerId) return { customerId: log.customerId };
        if (log.invoiceId) return { invoiceId: log.invoiceId };
        if (log.paymentId) return { paymentId: log.paymentId };
        if (log.lathamOrderId) return { lathamOrderId: log.lathamOrderId };
        return null;
    }

    getLogLabel(log: ISystemLogModel): string {
        // We no longer use this for the full label.
        // Keeping it minimal in case you call it elsewhere.
        return log.systemLogDetails?.[0]?.detailText
            ?? log.logText
            ?? 'System activity';
    }
    getMainLogText(log: ISystemLogModel): string {
        return log.systemLogDetails?.[0]?.detailText
            ?? log.logText
            ?? 'System activity';
    }

    getLogDateOnly(log: ISystemLogModel): string {
        const date = new Date(log.logDate);
        return date.toLocaleDateString([], {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        });
    }
    // ====================== OTHER METHODS ======================

    async search(searchString: string) {
        await this.functionLockService.lock("SCHEDULE_APPOINTMENT_SEARCH");
        this.searchResults = null;
        if ((searchString || '').length < 1) return;

        this.searchResults = await this.appointmentsService.scheduleAppointmentSearch(searchString);
        await this.functionLockService.release("SCHEDULE_APPOINTMENT_SEARCH");
    }

    async onSelect(scheduleAppointmentSearchModel: IScheduleAppointmentSearchModel) {
        this.appointmentId = scheduleAppointmentSearchModel.appointmentId;
        let jobModel = await this.jobsService.getJobByAppointmentId(this.appointmentId);
        jobModel = await this.jobEditDialogRef.openDialog(jobModel, this.appointmentId);

        if (!jobModel) return;

        const savedAppt = jobModel.appointments.find(x => x.appointmentId === this.appointmentId);
        if (savedAppt) {
            this.scheduleStore.currentDate = savedAppt.scheduledDateTime;
        }
    }

    openChat(e: Event) {
        e.stopImmediatePropagation();
        e.preventDefault();
        this.chatStore.isDialogClosed = false;
        this.chatStore.setSelectedChatGroupMessagesToRead();
        this.chatStore.refreshChatList();
        this.chatRef.openChat();
    }

    async logout() {
        this.authService.clearToken();
        this.lookupService.clearLookups();
        sessionStorage.removeItem("SELECTED_LOCATIONS");
        window.location.href = "/";
    }

    async clockIn() {
        try {
            this.slickScreenBlockerService.forceBlock();
            await SleepService.sleep(500);

            const actualCurrentTimeclockEntry = await this.timeclockService.getLastTimeclockEntry(GlobalsService.userInfo.userId);

            if (actualCurrentTimeclockEntry) {
                this.slickScreenBlockerService.forceUnblock();
                this.currentTimeclockEntry = actualCurrentTimeclockEntry;

                await Swal.fire({
                    icon: 'warning',
                    title: 'Oops',
                    text: 'You are already clocked in',
                    confirmButtonColor: '#007bff',
                    width: '28em',
                    heightAuto: false
                });
                return;
            }

            const timeclockEntryModel = new TimeclockEntryModel();
            timeclockEntryModel.userId = GlobalsService.userInfo.userId;
            timeclockEntryModel.clockIn = new Date();

            this.currentTimeclockEntry = await this.timeclockService.clockIn(timeclockEntryModel);
            this.timeclockStore.refreshCurrentEntry(this.currentTimeclockEntry);
        } finally {
            this.slickScreenBlockerService.forceUnblock();
        }
    }

    async clockOut() {
        try {
            this.slickScreenBlockerService.forceBlock();
            await SleepService.sleep(500);

            const lastTimeclockEntry = await this.timeclockService.getLastTimeclockEntry(GlobalsService.userInfo.userId);

            if (!lastTimeclockEntry) {
                this.slickScreenBlockerService.forceUnblock();
                this.currentTimeclockEntry = null;

                await Swal.fire({
                    icon: 'warning',
                    title: 'Oops',
                    text: 'You are not currently clocked in',
                    confirmButtonColor: '#007bff',
                    width: '28em',
                    heightAuto: false
                });
                return;
            }

            this.currentTimeclockEntry.clockOut = new Date();
            await this.timeclockService.clockOut(this.currentTimeclockEntry);

            this.currentTimeclockEntry = null;
            this.timeclockStore.refreshCurrentEntry(this.currentTimeclockEntry);
        } finally {
            this.slickScreenBlockerService.forceUnblock();
        }
    }

    async startBreak() {
        const newBreak = new TimeclockBreakModel();
        newBreak.breakStart = moment().toDate();
        newBreak.timeclockEntryId = this.currentTimeclockEntry.timeclockEntryId;
        await this.timeclockService.startBreak(newBreak);
        this.currentTimeclockEntry = await this.timeclockService.getLastTimeclockEntry(GlobalsService.userInfo.userId);
        this.recalcBreaks();
    }

    async endBreak() {
        this.currentTimeclockEntry.currentBreak.breakEnd = moment().toDate();
        await this.timeclockService.endBreak(this.currentTimeclockEntry.currentBreak);
        this.currentTimeclockEntry = await this.timeclockService.getLastTimeclockEntry(GlobalsService.userInfo.userId);
        this.recalcBreaks();
    }

    private recalcBreaks() {
        this.breaks = this.currentTimeclockEntry.breaks.map(b => {
            const timeclockStartEndModel = new TimeclockStartEndModel(b.breakStart, b.breakEnd, b.totalMinutes, b.uuid);
            timeclockStartEndModel.startGeolocation = b.breakStartGeolocation;
            timeclockStartEndModel.endGeolocation = b.breakEndGeolocation;
            return timeclockStartEndModel;
        });
        this.currentTimeclockEntry = this.timeclockService.recalc(this.currentTimeclockEntry);
    }
}